/**
 * @file blendLimits.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-22
 * @modified 2026-09-22
 * @description Inclusion limits for blend materials.
 *
 *              TWO STATES, AND THE DIFFERENCE MATTERS.
 *
 *              `status: 'approved'` — signed off, and ENFORCED by the
 *              calculator: over `warn` shows a warning, over `max` will not be
 *              accepted.
 *
 *              `status: 'proposed'` — a starting point for review, and enforced
 *              by NOTHING. It appears in the review document
 *              (scripts/blend-limits-review.js) and nowhere else. These figures
 *              are assembled from general ruminant nutrition practice, not from
 *              a source anyone here has signed off, and they govern what goes
 *              into feed that animals eat. They become 'approved' when somebody
 *              professionally responsible says so — not before.
 *
 *              Promoting one is a two-line change: set status to 'approved' and
 *              record who signed it off and when.
 *
 *              Keyed on pricing.blend_materials.key, which is stable; matching
 *              on the display name would break the moment someone renamed a row.
 */

/**
 * @typedef {object} InclusionLimit
 * @property {number}  warn     % above which to warn.
 * @property {number}  max      % above which to refuse. Infinity for advisory-only.
 * @property {'approved'|'proposed'} status
 * @property {string}  why      The reason, in a sentence somebody can argue with.
 * @property {string} [basis]   Where the number comes from.
 * @property {string} [signoff] Who approved it, and when.
 */

/** @type {Record<string, InclusionLimit>} */
export const INCLUSION_LIMITS = {
  // ── Approved and enforced ────────────────────────────────────────────────
  sodium_bicarb: {
    warn: 1.8,
    max: 2.0,
    status: 'approved',
    why: 'Rumen buffer. Beyond about 1.5–1.8% of the blend the sodium load starts to depress intake and lift dietary cation-anion difference, which matters most to dry cows and any DCAD-managed group.',
    basis: 'Set by Cam Seymour, 2026-09-22.',
    signoff: 'Cam Seymour, 2026-09-22'
  },

  // ── Proposed only. Nothing below is enforced. ────────────────────────────
  urea: {
    warn: 1.0,
    max: 1.5,
    status: 'proposed',
    why: 'Non-protein nitrogen. Needs a working rumen, adequate fermentable energy and a step-up period; excess releases ammonia faster than the rumen can use it. Never in anything for calves or for a blend that might reach them.',
    basis: 'Commonly capped at ~1% of concentrate in UK practice.'
  },
  salt: {
    warn: 1.0,
    max: 2.0,
    status: 'proposed',
    why: 'Palatability and water intake. Above ~1% of the blend it starts to drive water consumption and can put animals off the feed; it also stacks with the sodium in any bicarb present.',
    basis: 'Typical concentrate inclusion 0.5–1%.'
  },
  limestone: {
    warn: 2.0,
    max: 3.0,
    status: 'proposed',
    why: 'Calcium source. Generous calcium antagonises magnesium and phosphorus uptake, and high-calcium feed is the wrong answer entirely for a dry cow on a low-DCAD regime.',
    basis: 'Typical 1–2% of concentrate.'
  },
  molasses: {
    warn: 5.0,
    max: 8.0,
    status: 'proposed',
    why: 'Sugar and a binder. Past roughly 5% it is a handling and storage question as much as a nutritional one — heating, moulding and bridging in the bin — and the sugar adds to the rapid-fermentable load behind acidosis.',
    basis: 'Coarse-mix practice; liquid handling is usually the binding constraint.'
  },
  molacoat_coarse_mix_molasses: {
    warn: 5.0,
    max: 8.0,
    status: 'proposed',
    why: 'As molasses — the same sugar and the same handling limits.',
    basis: 'Treated as molasses.'
  },
  megalac_pspo_s_s: {
    warn: 3.0,
    max: 5.0,
    status: 'proposed',
    why: 'Rumen-protected fat. Total added oil much beyond 3% of the whole ration starts to interfere with fibre digestion even when the fat itself is protected.',
    basis: 'Total ration added fat commonly held under 3–4% of DM.'
  },
  butterfat: {
    warn: 3.0,
    max: 5.0,
    status: 'proposed',
    why: 'As above — it is the total added oil across the ration that matters, not the source.',
    basis: 'Counted against the same added-fat ceiling.'
  },
  palm_kernel: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'Variable oil content and poor palatability in quantity. Worth a separate look before it goes anywhere near a sheep blend, on copper grounds.',
    basis: 'Commonly held at or under 20–25% of a blend.'
  },
  us_maize_distillers: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'Distillers grains carry sulphur from the process. High inclusion across a whole ration is the usual route to polioencephalomalacia, and the sulphur in the water supply counts towards the same total.',
    basis: 'Total ration sulphur is the real limit; 20–25% of the blend is the usual proxy.'
  },
  wheat_distillers: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'As maize distillers — same sulphur consideration.',
    basis: 'Treated as maize distillers.'
  },
  rapeseed: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'Glucosinolates. Modern varieties are far lower than they were, but the ceiling is still conventional, and it matters more for younger stock.',
    basis: 'Commonly 20–25% of concentrate.'
  },
  novopro_rape: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'As rapeseed meal.',
    basis: 'Treated as rapeseed.'
  },
  rolled_beans: {
    warn: 20.0,
    max: 25.0,
    status: 'proposed',
    why: 'Tannins and other anti-nutritional factors; also a palatability ceiling.',
    basis: 'Commonly 20–25% of concentrate.'
  },
  rolled_wheat: {
    warn: 30.0,
    max: 40.0,
    status: 'proposed',
    why: 'Rapidly fermentable starch, and the classic route into sub-acute ruminal acidosis. The real limit is total ration starch, not this row on its own.',
    basis: 'Total ration starch commonly held under ~25% of DM.'
  },
  mole_maxi_wheat: {
    warn: 30.0,
    max: 40.0,
    status: 'proposed',
    why: 'As rolled wheat.',
    basis: 'Treated as wheat.'
  },
  wheat_gluten: {
    warn: 15.0,
    max: 20.0,
    status: 'proposed',
    why: 'Concentrated protein; a palatability and cost ceiling more than a safety one.',
    basis: 'Conventional.'
  }
};

/**
 * The limit that applies to a material — but ONLY where it has been signed off.
 *
 * A proposed figure returns nothing, so the calculator behaves as though it
 * does not exist. That is the whole point of the two states: an unreviewed
 * number must not quietly start refusing somebody's blend.
 */
export function enforcedLimitFor(materialKey) {
  const limit = INCLUSION_LIMITS[materialKey];
  return limit && limit.status === 'approved' ? limit : null;
}

/**
 * How a typed inclusion stands against its limit.
 * Returns null where there is nothing to say.
 */
export function checkInclusion(materialKey, value) {
  const limit = enforcedLimitFor(materialKey);
  if (!limit) return null;
  const pct = parseFloat(value);
  if (!Number.isFinite(pct)) return null;

  if (pct > limit.max) {
    return { level: 'over', limit, message: `Over the ${limit.max}% limit for this material.` };
  }
  if (pct > limit.warn) {
    return { level: 'warn', limit, message: `Above the ${limit.warn}% guideline — ${limit.max}% is the limit.` };
  }
  return null;
}

/** Everything, for the review document. Approved first, then alphabetical. */
export function allLimits() {
  return Object.entries(INCLUSION_LIMITS)
    .map(([key, limit]) => ({ key, ...limit }))
    .sort((a, b) =>
      (a.status === b.status ? a.key.localeCompare(b.key) : a.status === 'approved' ? -1 : 1));
}
